import {catchError, map, mergeMap, take} from 'rxjs/operators';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ProgressComponent} from '../progress/progress.component';
import {Subscription, throwError, throwError as observableThrowError} from 'rxjs';
import {MatPaginator} from '@angular/material/paginator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {RouterLink} from '@angular/router';
import {BreadCrumb} from '../../models/breadcrumb';
import {User} from '../../models/user';
import {UsersService} from '../../services/users.service';
import {ExportUser} from '../../models/export-user';
import {collection, collectionData, DocumentData, Firestore, orderBy, query} from '@angular/fire/firestore';
import {Auth, authState} from '@angular/fire/auth';
import {MatCard, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatButton, MatMiniFabButton} from '@angular/material/button';
import {I18nPluralPipe} from '@angular/common';
import {MatError} from '@angular/material/input';
import {stringify} from 'csv-stringify/browser/esm/sync';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    imports: [
        ProgressComponent,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        RouterLink,
        MatButton,
        I18nPluralPipe,
        MatError,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatCellDef,
        MatHeaderCell,
        MatCell,
        MatMiniFabButton,
        MatHeaderRowDef,
        MatRowDef,
        MatHeaderRow,
        MatRow
    ],
    standalone: true
})
export class UsersComponent implements OnInit, OnDestroy {
    public displayedColumns = [
        'email',
        'role',
        'actions'
    ];
    public generalErrorMessage = '';
    userPluralMapping = {
        '=0': '0 Users',
        '=1': '1 User',
        other: '# Users'
    };
    dataSource: MatTableDataSource<DocumentData>;
    breadcrumbs: BreadCrumb[];
    @ViewChild(ProgressComponent, {static: true}) progress: ProgressComponent;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;
    private userId: string;
    admin = false;
    private authStateSubscription: Subscription;
    private subscription: Subscription;

    constructor(private firestore: Firestore,
                private userService: UsersService,
                private fireAuth: Auth,
                private snackbar: MatSnackBar) {
    }

    ngOnInit() {
        this.breadcrumbs = [
            {url: ['/'], title: 'Accounts'},
            {url: null, title: 'Users'},
        ];

        this.authStateSubscription = authState(this.fireAuth).pipe(
            take(1),
            mergeMap((user) => {
                this.userId = user.uid;
                // We don't refresh the token here because it will get called often
                return user.getIdTokenResult();
            }),
        ).subscribe(token => {
            this.admin = !!token.claims.admin;
        });

        const usersColRef = collection(this.firestore, 'users');
        const usersQuery = query(usersColRef, orderBy('email', 'asc'));
        this.subscription = collectionData(usersQuery, {idField: 'id'}).pipe(
            catchError(e => throwError(() => e))
        ).subscribe({
            next: data => {
                this.dataSource = new MatTableDataSource(data);
                this.dataSource.sort = this.sort;
                this.dataSource.paginator = this.paginator;
            }, error: error => {
                console.log(error);
            }
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.authStateSubscription.unsubscribe();
    }

    search(query: string) {
        this.dataSource.filter = query.trim().toLowerCase();
    }

    deleteUser(user: User) {
        if (this.userId === user.id) {
            this.snackbar.open('You can\'t delete your own account', null, {duration: 3000});
            return;
        }
        if (this.admin) {
            this.progress.setVisible(true);
            this.userService.deleteUser(user.id).pipe(
                map(() => {
                    return;
                }),
                catchError((error: Response | any) => {
                    return observableThrowError(error);
                }))
                .subscribe({
                        next: () => {
                            this.progress.setVisible(false);
                        },
                        error: (error) => {
                            this.progress.setVisible(false);
                            this.generalErrorMessage = error.toString();
                        }
                    }
                );
        } else {
            this.snackbar.open('Permission denied. Please contact an administrator', null, {duration: 3000});
        }
    }

    downloadCsv() {
        const exports: ExportUser[] = [];
        for (const item of this.dataSource.data) {
            const tmp: ExportUser = {} as ExportUser;
            tmp.email = item.email;
            tmp.admin = item.admin ? 'Yes' : 'No';
            tmp.operator = item.operator ? 'Yes' : 'No';
            tmp.user = item.user ? 'Yes' : 'No';
            exports.push(tmp);
        }
        const csv = stringify(exports, {
            header: true,
            columns: [
                {key: 'email', header: 'email'},
                {key: 'admin', header: 'admin'},
                {key: 'operator', header: 'operator'},
                {key: 'user', header: 'user'},
            ]
        });
        this.download('users-export.csv', csv);
    }

    download(filename, text) {
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(text));
        element.setAttribute('download', filename);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }
}
